import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EcommerceProductsProvider, useEcommerceProducts, ProductPrice, type EcommerceProduct } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wpQueryParams = {
  per_page: 6,
  product_cat: "16,17",
  orderby: "date",
  order: "desc"
};
const wpQuery = new WP_Query(wpQueryParams);
function getConditionLabel(product: EcommerceProduct): string {
  if (product.categories && product.categories.length > 0) {
    const isUsed = product.categories.some(c => c.id === 17 || c.slug.toLowerCase().includes("used") || c.name.toLowerCase().includes("used"));
    if (isUsed) return "Used";
    const isNew = product.categories.some(c => c.id === 16 || c.slug.toLowerCase().includes("new") || c.name.toLowerCase().includes("new"));
    if (isNew) return "New";
    return product.categories[0].name;
  }
  return "Unit";
}
function InventoryGrid() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="border border-border bg-card p-5">
            <Skeleton className="aspect-4/3 w-full rounded-none" />
            <div className="mt-4 space-y-2">
              <Skeleton className="h-4 w-24 rounded-none" />
              <Skeleton className="h-6 w-3/4 rounded-none" />
              <Skeleton className="h-5 w-1/3 rounded-none" />
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <Skeleton className="h-9 w-full rounded-none" />
              <Skeleton className="h-9 w-full rounded-none" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Unable to retrieve machine inventory records.
        </p>
        <Button type="button" variant="outline" onClick={() => refetch()} className="mt-4 rounded-none text-xs font-mono uppercase tracking-wider">
          Retry Connection
        </Button>
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="border border-border bg-card p-12 text-center">
        <p className="font-mono text-sm uppercase tracking-wider text-muted-foreground">
          No mechanical bull units currently listed in this category.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => {
      const condition = getConditionLabel(product);
      const inStock = product.is_in_stock !== false;
      return <article key={product.id} data-index={i} className="flex flex-col border border-border bg-card transition-colors duration-180 hover:border-foreground">
            <div className="relative aspect-4/3 w-full overflow-hidden border-b border-border bg-muted p-4">
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" className="h-full w-full object-contain" /> : <div className="flex h-full w-full items-center justify-center font-mono text-xs uppercase text-muted-foreground">
                  Unit photography pending
                </div>}
              <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                <Badge variant={condition === "New" ? "default" : "secondary"} className="rounded-none font-mono text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5">
                  {condition}
                </Badge>
              </div>
            </div>

            <div className="flex flex-1 flex-col p-5">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Stock Status
                </span>
                <span className={`inline-flex items-center font-mono text-xs font-medium uppercase tracking-wider ${inStock ? "text-foreground" : "text-muted-foreground"}`}>
                  <span className={`mr-1.5 inline-block h-1.5 w-1.5 ${inStock ? "bg-primary" : "bg-muted-foreground"}`} />
                  {inStock ? "Available" : "Reserved / Sold"}
                </span>
              </div>

              <h3 className="mt-3 font-serif text-2xl font-bold uppercase tracking-tight text-foreground">
                {product.title}
              </h3>

              <div className="mt-2 border-t border-border pt-3">
                <span className="block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                  Unit Price
                </span>
                <ProductPrice product={product} className="mt-0.5 font-serif text-2xl font-bold text-foreground" />
              </div>

              <div className="mt-6 flex flex-col gap-2 pt-2">
                <Button asChild variant="outline" className="w-full rounded-none border-border font-mono text-xs font-semibold uppercase tracking-wider text-foreground hover:border-foreground hover:bg-transparent">
                  <Link to={product.link}>View Details</Link>
                </Button>

                <Button asChild className="w-full rounded-none bg-primary font-mono text-xs font-semibold uppercase tracking-wider text-primary-foreground hover:bg-primary/90">
                  <Link to="/RequestQuotePage">
                    Enquire About This Bull
                  </Link>
                </Button>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function Inventory() {
  return <section id="current-inventory-prices" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 max-w-3xl">
          <p className="font-mono text-xs font-bold uppercase tracking-widest text-primary">
            Current Inventory
          </p>
          <h2 className="mt-2 font-serif text-4xl font-bold uppercase tracking-tight sm:text-5xl">
            Current Mechanical Bull Prices
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            The examples below come from the machines currently listed on
            Mechanical Bull Shop. Because inventory and prices can change, use
            the individual product page as the current source for each machine
            rather than treating these figures as permanent market averages.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 6,
        product_cat: [16, 17],
        orderby: "date",
        order: "desc"
      }}>
          <InventoryGrid />
        </EcommerceProductsProvider>
      </div>
    </section>;
}